import { Box, Button, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

/**
 * App-wide React error boundary.
 *
 * Why this exists: a render-time throw anywhere in the tree (e.g. the macOS
 * "TypeError: Buffer is already detached" crash in the PDF reader) used to
 * propagate all the way to the root with NO boundary in place, so React
 * unmounted the whole app and the user saw a **silent white screen** — no
 * indication anything had gone wrong. This catches that throw and renders a
 * real "something went wrong" surface with a Reload action and the error
 * details, and logs to the console so it's visible in the desktop webview
 * inspector.
 *
 * Two variants:
 *   • `"page"` — wraps the routed `<Outlet>` inside `AppShell`; the nav bar
 *     stays interactive, the error shows in the content area, and it
 *     auto-resets when the route changes (`resetKey`), so the user can click
 *     away without a reload.
 *   • `"app"` — the last-resort full-screen net at the very top of the tree
 *     (in `main.tsx`), catching throws from the router/shell themselves.
 *
 * Pinned by `ErrorBoundary.test.tsx`.
 */
interface ErrorBoundaryProps {
  children: ReactNode;
  /** Full-screen ("app") last-resort net, or in-page ("page") inside the
   * shell. Defaults to "page". */
  variant?: "page" | "app";
  /** When this changes, a caught error is cleared. The route wrapper passes
   * the pathname so navigating away from a broken page recovers it. */
  resetKey?: unknown;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidUpdate(prev: ErrorBoundaryProps): void {
    // Recover on navigation: once the resetKey (route pathname) changes, drop
    // the caught error so the new page renders instead of the stale fallback.
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Make the fatal error visible (devtools / desktop webview inspector) —
    // the app no longer fails silently.
    // eslint-disable-next-line no-console
    console.error(
      "[ErrorBoundary] Uncaught render error:",
      error,
      info.componentStack,
    );
  }

  private readonly handleReload = (): void => {
    window.location.reload();
  };

  private readonly handleDismiss = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <ErrorFallback
        error={error}
        variant={this.props.variant ?? "page"}
        onReload={this.handleReload}
        onDismiss={this.handleDismiss}
      />
    );
  }
}

/** Themed fallback surface shown when the boundary catches an error. */
function ErrorFallback({
  error,
  variant,
  onReload,
  onDismiss,
}: {
  error: Error;
  variant: "page" | "app";
  onReload: () => void;
  onDismiss: () => void;
}): JSX.Element {
  const card = (
    <Box
      role="alert"
      data-testid="error-boundary-fallback"
      sx={{
        maxWidth: 560,
        width: "100%",
        mx: "auto",
        borderRadius: 2,
        border: 1,
        borderColor: (t) => alpha(t.palette.error.main, 0.4),
        bgcolor: (t) => alpha(t.palette.error.main, 0.06),
        p: 3,
        display: "grid",
        gap: 1.5,
      }}
    >
      <Typography
        component="h2"
        sx={{ fontSize: "1.125rem", fontWeight: 600, color: "error.main" }}
      >
        Something went wrong
      </Typography>
      <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
        This part of the app hit an unexpected error and couldn’t be displayed.
        Your work elsewhere is unaffected — try again, or reload the app.
      </Typography>

      {/* The actual error message, plain and selectable, so a user can read it
          to support instead of seeing a blank screen. */}
      <Box
        component="pre"
        data-testid="error-boundary-message"
        sx={{
          m: 0,
          p: 1.5,
          borderRadius: 1,
          bgcolor: (t) => alpha(t.palette.text.primary, 0.04),
          border: 1,
          borderColor: "divider",
          fontSize: 12.5,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          color: "text.primary",
        }}
      >
        {error.message || String(error)}
      </Box>

      {error.stack && (
        <Box
          component="details"
          sx={{
            fontSize: 12,
            color: "text.disabled",
            "& summary": { cursor: "pointer", userSelect: "none" },
            "& pre": {
              mt: 1,
              maxHeight: 200,
              overflow: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            },
          }}
        >
          <summary>Technical details</summary>
          <pre>{error.stack}</pre>
        </Box>
      )}

      <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
        <Button variant="contained" color="primary" size="small" onClick={onReload}>
          Reload app
        </Button>
        {/* "Try again" re-renders the children in place — useful for a
            transient error; the route-change auto-reset covers navigation. */}
        <Button variant="outlined" color="inherit" size="small" onClick={onDismiss}>
          Try again
        </Button>
      </Stack>
    </Box>
  );

  if (variant === "app") {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          p: 3,
          bgcolor: "background.default",
        }}
      >
        {card}
      </Box>
    );
  }
  return <Box sx={{ py: 4 }}>{card}</Box>;
}

/**
 * Route-scoped boundary for the content `<Outlet>`. Reads the current path so
 * a caught error clears when the user navigates to another route (the app
 * shell + nav stay mounted and interactive the whole time).
 */
export function RouteErrorBoundary({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const { pathname } = useLocation();
  return (
    <ErrorBoundary variant="page" resetKey={pathname}>
      {children}
    </ErrorBoundary>
  );
}
